import {
	ElButton,
	ElDivider,
	ElDrawer,
	ElEmpty,
	ElForm,
	ElIcon,
	ElInput,
	ElLink,
	ElMessage,
	ElMessageBox,
	ElOption,
	ElPagination,
	ElPopover,
	ElScrollbar,
	ElSelect,
	ElTabPane,
	ElTabs,
	PaginationRef,
	useForm,
} from '@qsxy/element-plus-react';
import { useRequest } from 'ahooks';
import { ConversaionDetail, delConversations, getConversations } from './api';
import { RootState } from '@/typings';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TooltipRef } from '@qsxy/element-plus-react/dist/Tooltip/typings';
import { useAppContext } from '@/AppContext';
import './style.scss';
import { conversatonType } from '@/config';
import dayjs from 'dayjs';
import { renameLocalConversation } from '../chat/api';

const HistoryMenu = () => {
	const loginUser = useSelector<RootState, RootState['user']>(state => state.user ?? {});

	const [form] = useForm<ConversaionDetail>();
	const pageRef = useRef<PaginationRef>(null);

	const { data: { content: tableData = [], totalPages = 0 } = {}, run } = useRequest(
		(params: { pageNum?: number; conversation_type?: string }) => getConversations({ user_id: loginUser.username, ...params }, loginUser?.isVisitor),
		{
			manual: true,
		},
	);

	/** 查询方法 */
	const onQuery = useCallback(
		(p: { pageNum?: number; name?: string; conversation_type?: string } = {}) => {
			const params = form.getFieldsValue();
			run({ pageNum: pageRef?.current?.pageNum, ...p, ...params, conversation_type: params.conversation_type === 'all' ? '' : params.conversation_type });
		},
		[form, run],
	);

	const navigate = useNavigate();
	const { conversationTitle, localConversationId: conversationId, refreshConversation } = useAppContext();

	const [visible, setVisible] = useState(false);

	const tooltipRef = useRef<TooltipRef>(null);

	const onOpen = useCallback(() => {
		tooltipRef.current.hide();
		setVisible(true);
	}, []);

	const onClose = useCallback(() => {
		setVisible(false);
	}, []);

	const handleChat = async ({ id, conversation_id, title: name }: ConversaionDetail) => {
		tooltipRef.current.hide();
		setVisible(false);
		conversationTitle.current = name;
		conversationId.current = loginUser?.isVisitor ? null : id;
		navigate(`/chat/${loginUser?.isVisitor ? id : conversation_id}`);
	};

	const handleDelConversation = (conversation_id: string) => {
		ElMessageBox.confirm('您确定要删除选择的记录吗？', '删除对话', {
			beforeClose: (action, done, instance) => {
				if (action === 'confirm') {
					instance.current.setConfirmButtonLoading(true);
					instance.current.setConfirmButtonText('执行中...');
					delConversations(conversation_id)
						.then(res => {
							instance.current.setConfirmButtonText('确定');
							instance.current.setConfirmButtonLoading(false);
							if (res) {
								onQuery({ pageNum: 1 });
								done();
								ElMessage.success('删除成功');
							} else {
								ElMessage.error('删除失败，请重试');
							}
						})
						.catch(() => ElMessage.error('删除失败，请重试'));
				} else {
					done();
				}
			},
		}).then();
	};

	const handleEditTitle = (item: ConversaionDetail) => {
		ElMessageBox.prompt('请输入标题', '修改标题', {
			inputValue: item.title,
			inputErrorMessage: '请输入标题',
			inputValidator: value => !!value,
		}).then(({ value }) => {
			renameLocalConversation({ id: item.id, title: value }).then(() => {
				onQuery();
				ElMessage.success('修改成功');
			});
		});
	};

	useEffect(() => {
		refreshConversation.current = () => onQuery({ pageNum: 1 });
		if (loginUser?.username) {
			onQuery({ pageNum: 1 });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loginUser?.username]);

	return (
		<>
			<ElPopover
				ref={tooltipRef}
				trigger="hover"
				popperClass="ttn-history-popper"
				placement="right-start"
				content={
					<>
						<ElDivider style={{ color: 'var(--el-text-color-secondary)' }}>会话历史记录</ElDivider>
						<div className="ttn-history-container">
							{tableData.slice(0, 5).map(item => (
								<div className="ttn-history-item" key={item.id} onClick={() => handleChat(item)}>
									<ElIcon name="message" prefix="fas" />
									<span>{item.title}</span>
								</div>
							))}
						</div>
						{!loginUser?.isVisitor ? (
							<div className="view-all">
								<ElIcon name="calendar-lines" prefix="fas" />
								<ElLink type="primary" underline onClick={onOpen}>
									全部历史记录
								</ElLink>
							</div>
						) : null}
					</>
				}
			>
				<li>
					<ElLink className="ttn-menu-item">
						<ElIcon prefix="fas" name="calendar-lines"></ElIcon>
						<span>历史记录</span>
					</ElLink>
				</li>
			</ElPopover>

			<ElDrawer visible={visible} close={onClose} direction="bottom" size="80%">
				<ElDrawer.body className="ttn-all-history">
					<div className="header">
						<span>历史记录</span>
						<ElForm<ConversaionDetail> form={form}>
							<ElForm.Item name="title" noStyle>
								<ElInput
									prefix={<ElIcon prefix="fal" name="search" />}
									style={{ width: 172 }}
									onKeyUp={event => {
										if (event.key === 'Enter') {
											onQuery({ pageNum: 1 });
										}
									}}
									onClear={() => onQuery({ pageNum: 1 })}
								/>
							</ElForm.Item>

							<ElForm.Item name="conversation_type" noStyle style={{ width: 'unset' }}>
								<ElSelect style={{ width: 160 }} onChange={() => onQuery({ pageNum: 1 })}>
									<ElOption value={'all'} label={'全部'} />
									{conversatonType.map((item, index) => (
										<ElOption key={item} value={index + ''} label={item} />
									))}
								</ElSelect>
							</ElForm.Item>
						</ElForm>

						{/* <ElButton>
							<ElIcon name="trash" prefix="fas" />
							&nbsp;删除所有历史记录
						</ElButton> */}
					</div>

					<ElTabs>
						<ElTabPane
							title={
								<>
									<ElIcon name="message" prefix="fas" />
									<span style={{ marginLeft: 8 }}>会话历史</span>
								</>
							}
							name={'conversation'}
						>
							{tableData.length > 0 ? (
								<ElScrollbar height="calc(-170px + 80vh)">
									{tableData.map(item => (
										<div className="ttn-history-item" key={item.id}>
											<div className="create-time">{dayjs(item.created_at, 'millisecond').format('YYYY-MM-DD')}</div>
											<div className="content">
												<div className="title" onClick={() => handleChat(item)}>
													<ElIcon name="message" prefix="fas" />
													<ElLink>{item.title}</ElLink>
												</div>
												<div className="footer">
													<span>{dayjs(item.created_at, 'millisecond').format('HH:mm:ss')}</span>
													<div className="ope-btn">
														<ElButton text type="info" onClick={() => handleEditTitle(item)}>
															<ElIcon name="pen" prefix="fas" />
														</ElButton>
														<ElButton text type="info" onClick={() => handleDelConversation(item.id)}>
															<ElIcon name="trash" prefix="fas" />
														</ElButton>
													</div>
												</div>
											</div>
										</div>
									))}
									<div className="ttn-table-pagination">
										<ElPagination
											ref={pageRef}
											total={totalPages}
											showTotal={() => false}
											showQuickJumper
											showSizeChanger={false}
											defaultPageSize={10}
											onChange={pageNum => onQuery?.({ pageNum })}
										/>
									</div>
								</ElScrollbar>
							) : (
								<ElEmpty imageSize={120} />
							)}
						</ElTabPane>
					</ElTabs>
				</ElDrawer.body>
			</ElDrawer>
		</>
	);
};

export default HistoryMenu;
